import Projections from 'pages/MasterCampaignPlanPage/new/components/Projections/Projections'
import React, { FC, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'

import { EuiTabbedContent, EuiLoadingContent, EuiTitle, EuiSpacer } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { useGetBillboardsByZipCodesQuery } from 'api/billboardApi'
import { useGetDemographicAndHousingCharacteristicsQuery, useGetEducationalAttainmentQuery, useGetFinancialCharacteristicsQuery } from 'api/censusDataApi'
import { useLazyGetDomainsQuery } from 'api/domainsApi'
import { useGetMasterCampaignPlanQuery } from 'api/masterCampaignPlansApi'

import { useBillboardZipCodes, useHouseholdZipCodes } from '../../../utils/useProjectionsUtils'
import ExecutiveSummary from './components/ExecutiveSummary/ExecutiveSummary'
import MarketAnalysis from './components/MarketAnalysis'
import MediaPlan from './components/MediaPlan/MediaPlan'
import PageContent from './components/PageContent'
import Personas from './components/Personas/Personas'

export interface MasterCampaignPlanPageProps {}

interface MasterCampaignPlanPageParams {
  id: string
}

const MasterCampaignPlanPage: FC<MasterCampaignPlanPageProps> = () => {
  const { id } = useParams<MasterCampaignPlanPageParams>()

  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching, error: error } = useGetMasterCampaignPlanQuery({ publicId: id ?? '' }, { skip: !id })

  const masterCampaignStub = masterCampaignPlan?.masterCampaignStub

  const billboardZipCodes = useBillboardZipCodes(masterCampaignStub)
  const householdZipCodes = useHouseholdZipCodes(masterCampaignStub)

  useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  useGetDemographicAndHousingCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })
  useGetEducationalAttainmentQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })
  useGetFinancialCharacteristicsQuery({ zipCodes: householdZipCodes }, { skip: householdZipCodes.length === 0 })

  const [triggerGetDomains, domainsResult] = useLazyGetDomainsQuery()

  useEffect(() => {
    if (!masterCampaignStub) {
      return
    }

    triggerGetDomains()
  }, [masterCampaignStub])

  useEffect(() => {
    if (!domainsResult?.data || domainsResult.data.length <= 0) {
      return
    }

    for (const domain of domainsResult.data) {
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(domain.logoUrl)
        img.onerror = () => reject()

        img.src = domain.logoUrl
      })
    }
  }, [domainsResult])

  const getTabFontSize = () => {
    return window.innerWidth > 1102 ? '16px' : '13px'
  }

  if (!masterCampaignPlan) {
    return <EuiLoadingContent />
  }

  const allTabs: EuiTabbedContentTab[] = [
    {
      id: 'executive-summary',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Executive Summary</div>
        </div>
      ),
      content: (
        <PageContent>
          <ExecutiveSummary masterCampaignPlan={masterCampaignPlan} />
        </PageContent>
      )
    },
    {
      id: 'market-analysis',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Analysis & Strategy</div>
        </div>
      ),
      content: (
        <PageContent>
          <MarketAnalysis accountName={masterCampaignPlan.accountName} marketAnalysisContent={masterCampaignPlan.marketAnalysisContent} />
        </PageContent>
      )
    },
    {
      id: 'personas',
      name: (
        <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>
          <div>Personas</div>
        </div>
      ),
      content: (
        <PageContent>
          <Personas masterCampaignPlan={masterCampaignPlan} />
        </PageContent>
      )
    },
    {
      id: 'media-plan',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Media Plan</div>,
      content: (
        <PageContent>
          <MediaPlan masterCampaignPlan={masterCampaignPlan} />
        </PageContent>
      )
    },
    {
      id: 'projections',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Projections</div>,
      disabled: !masterCampaignPlan?.tvNetworkDistribution || !masterCampaignPlan?.internetMarginOfError || !masterCampaignPlan?.tvMarginOfError || !masterCampaignPlan?.billboardMarginOfError || !masterCampaignPlan.masterCampaignStub.campaignBuilders.some(cb => cb.cpm),
      content: (
        <PageContent>
          <Projections masterCampaignPlan={masterCampaignPlan} masterCampaignStub={masterCampaignPlan.masterCampaignStub} />
        </PageContent>
      )
    }
  ]

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'executive-summary' && masterCampaignPlan.clientPreviewShowExecutiveSummary === false) return false
    if (tab.id === 'market-analysis' && masterCampaignPlan.clientPreviewShowMarketAnalysis === false) return false
    if (tab.id === 'personas' && masterCampaignPlan.clientPreviewShowPersonas === false) return false
    if (tab.id === 'media-plan' && masterCampaignPlan.clientPreviewShowMediaPlan === false) return false
    if (tab.id === 'projections' && masterCampaignPlan.clientPreviewShowProjections === false) return false
    return true
  })

  return (
    <React.Fragment>
      <Helmet>
        <title>Master Campaign Plan - {masterCampaignPlan.name}</title>
      </Helmet>

      <EuiTitle>
        <h2>{masterCampaignPlan?.name}</h2>
      </EuiTitle>

      <EuiSpacer />

      <EuiTabbedContent tabs={tabs} initialSelectedTab={tabs[0]} className='master-campaign-plan-tabs' />
    </React.Fragment>
  )
}

export default MasterCampaignPlanPage
